import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import root from 'react-shadow';
import { Box } from '../ui';
import { RootState } from '../../types';
import { sanitizeHtml as sanitizeRenderedHtml } from '../../utils/sanitizeHtml';

interface RichHtmlViewerProps {
  content: string;
  sx?: any;
}

/**
 * Renders raw HTML inside a Shadow DOM.
 *
 * Why a shadow DOM:
 *  - Imported lessons (Thought Industries, etc.) ship with their own <style> blocks,
 *    custom classes, <div>/<form>/<input> structures, etc. that ProseMirror/Tiptap
 *    would silently strip on parse.
 *  - Shadow DOM scopes the embedded <style> rules so they don't leak out into the
 *    rest of the LMS, and inheritable host styles (font-family, color, .App rules)
 *    don't leak in to break the imported layout.
 *
 * Why not Tiptap for viewing:
 *  - Tiptap's schema only knows paragraph, heading 1-3, lists, marks, etc.
 *    Anything else is dropped (divs, classes, the entire <style> block).
 *  - Tiptap-authored content is plain semantic HTML, so the baseline tag-selector
 *    styles below render it correctly without any of Tiptap's runtime.
 *
 * Trust model:
 *  - Lesson content can only be created by Admin/AccountAdmin/WorkspaceAdmin, but
 *    importer-sourced HTML originates from third parties. We sanitize defensively:
 *    strip <script>, <object>, <embed>, <base>, <meta>, <link>, on* event handlers,
 *    and javascript: URIs in href/src/action/formaction/srcdoc/xlink:href.
 *  - We intentionally KEEP <style> tags — they are scoped to the shadow root and
 *    are required for imported content to render correctly.
 *  - Legacy imports (Thought Industries) shipped inline <script> that built an
 *    iframe URL from a `window.CONF...currentUser` global. Those scripts never
 *    run here (stripped below, and innerHTML doesn't execute scripts anyway), so
 *    before stripping we interpret that one idiom — read the identity fields off
 *    the Redux profile and write the resolved src straight onto the iframe. No
 *    script is executed; it is pure string parsing. Anything richer than URL
 *    assembly can't be reproduced and is reported (see reportContentIssue)
 *    instead of failing silently. Go-forward content should use
 *    {{firstName}}/{{lastName}}/{{email}} tokens rather than scripts.
 *
 * Specificity strategy for the baseline CSS:
 *  - Tag selectors only (specificity 0,0,1), no !important.
 *  - Imported <style> blocks use class selectors (specificity 0,1,0), so any rule
 *    in the imported content automatically wins. This means one viewer renders both
 *    Tiptap-authored and imported HTML correctly without needing a flag.
 */

const URL_ATTRS = ['href', 'src', 'action', 'formaction', 'srcdoc', 'xlink:href'];

// Identity fields we can supply to migrated content — both the legacy
// `window.CONF...currentUser` global and the go-forward {{token}} form.
type IdentityUser = { firstName?: string; lastName?: string; email?: string } | null;
const IDENTITY_FIELDS: Record<string, keyof NonNullable<IdentityUser>> = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
};
const CONF_USER_PREFIX = 'window.CONF.preload.currentUser.currentUser.';

// Single hook for content-resolution issues. The failure mode here is a SILENT
// no-op — a stripped legacy script that did more than assemble a URL leaves an
// inert iframe with nothing thrown for a crash reporter to catch — so warnings
// are pushed to Sentry explicitly. Successes stay console-only to avoid noise.
type ContentIssueLevel = 'info' | 'warn';
function reportContentIssue(level: ContentIssueLevel, kind: string, detail: string): void {
  const msg = `[lesson-content] ${kind}: ${detail}`;
  if (level === 'warn') {
    console.warn(msg);
    Sentry.captureMessage(msg, 'warning');
  } else {
    console.info(msg);
  }
}

// Split a JS expression on top-level '+' (string concatenation), ignoring '+'
// inside quotes or parentheses.
function splitTopLevelPlus(expr: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let quote: string | null = null;
  let cur = '';
  for (const ch of expr) {
    if (quote) {
      cur += ch;
      if (ch === quote) quote = null;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
      cur += ch;
    } else if (ch === '(') {
      depth++;
      cur += ch;
    } else if (ch === ')') {
      depth--;
      cur += ch;
    } else if (ch === '+' && depth === 0) {
      parts.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  parts.push(cur);
  return parts.map((p) => p.trim()).filter(Boolean);
}

// Split script source into statements on ';' / newline, ignoring separators
// inside string literals.
function splitStatements(src: string): string[] {
  const stmts: string[] = [];
  let quote: string | null = null;
  let cur = '';
  for (const ch of src) {
    if (quote) {
      cur += ch;
      if (ch === quote) quote = null;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
      cur += ch;
    } else if (ch === ';' || ch === '\n') {
      if (cur.trim()) stmts.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  if (cur.trim()) stmts.push(cur.trim());
  return stmts;
}

// Resolve the narrow set of expressions the legacy TI scripts use: string
// literals, encodeURIComponent(...), the CONF identity path, prior variables,
// and '+' concatenation of those. Anything else throws → the block is reported
// and left inert rather than half-resolved.
function resolveExpr(expr: string, vars: Record<string, string>, user: IdentityUser): string {
  const trimmed = expr.trim();

  const parts = splitTopLevelPlus(trimmed);
  if (parts.length > 1) {
    return parts.map((p) => resolveExpr(p, vars, user)).join('');
  }

  const enc = trimmed.match(/^encodeURIComponent\(([\s\S]*)\)$/);
  if (enc) return encodeURIComponent(resolveExpr(enc[1], vars, user));

  const literal = trimmed.match(/^(['"])([\s\S]*)\1$/);
  if (literal) return literal[2];

  if (trimmed.startsWith(CONF_USER_PREFIX)) {
    const field = IDENTITY_FIELDS[trimmed.slice(CONF_USER_PREFIX.length)];
    if (!field) throw new Error(`unsupported CONF field ${trimmed}`);
    return user?.[field] ?? '';
  }

  const ident = trimmed.replace(/^window\./, '');
  if (Object.prototype.hasOwnProperty.call(vars, ident)) return vars[ident];

  throw new Error(`unresolvable expression ${trimmed}`);
}

// Interpret the legacy "build an iframe src from window.CONF" idiom and write
// the resolved URL onto the iframe, BEFORE the <script> is stripped below.
function resolveLegacyConfForms(doc: Document, user: IdentityUser): void {
  Array.from(doc.querySelectorAll('script')).forEach((script) => {
    const code = script.textContent || '';
    if (!code.includes('window.CONF')) return;

    try {
      const vars: Record<string, string> = {};
      const targets: Array<{ id: string; expr: string }> = [];

      splitStatements(code).forEach((stmt) => {
        const srcAssign = stmt.match(/getElementById\((['"])([^'"]+)\1\)\s*\.\s*src\s*=\s*([\s\S]+)$/);
        if (srcAssign) {
          targets.push({ id: srcAssign[2], expr: srcAssign[3] });
          return;
        }
        const assign = stmt.match(/^(?:window\.)?([A-Za-z_$][\w$]*)\s*=\s*([\s\S]+)$/);
        if (assign) vars[assign[1]] = resolveExpr(assign[2], vars, user);
      });

      if (targets.length === 0) throw new Error('no iframe src assignment found');

      targets.forEach((t) => {
        const el = doc.getElementById(t.id);
        if (!el) throw new Error(`target #${t.id} not found`);
        el.setAttribute('src', resolveExpr(t.expr, vars, user));
      });
      reportContentIssue('info', 'legacy-form-resolved', `#${targets.map((t) => t.id).join(', #')}`);
    } catch (err) {
      reportContentIssue('warn', 'legacy-script-unresolved', `${(err as Error).message} — iframe left inert`);
    }
  });
}

// Go-forward substitution: {{firstName}}/{{lastName}}/{{email}} inside URL
// attributes, URL-encoded. Unknown tokens are reported and left as-is.
function substituteIdentityTokens(doc: Document, user: IdentityUser): void {
  doc.querySelectorAll('*').forEach((el) => {
    URL_ATTRS.forEach((attr) => {
      const val = el.getAttribute(attr);
      if (!val || !val.includes('{{')) return;
      const next = val.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
        const field = IDENTITY_FIELDS[name];
        if (!field) {
          reportContentIssue('warn', 'unknown-token', `${match} in ${attr}`);
          return match;
        }
        return encodeURIComponent(user?.[field] ?? '');
      });
      el.setAttribute(attr, next);
    });
  });
}

function sanitizeHtml(html: string, user: IdentityUser): string {
  if (!html) return '';

  // DOMParser does not execute scripts on parse.
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Resolve migrated content's dynamic bits BEFORE stripping <script>: the
  // legacy CONF idiom needs the script text, and both paths need the user.
  resolveLegacyConfForms(doc, user);
  substituteIdentityTokens(doc, user);

  // Sanitize AFTER resolving legacy URLs so no derived value bypasses the gate.
  return sanitizeRenderedHtml(doc.body.innerHTML);
}

// Baseline styles for plain semantic HTML (Tiptap-authored content).
// Imported content with its own <style> block beats these by specificity.
const baselineStyles = `
  :host {
    all: initial;
    display: block;
  }

  * {
    box-sizing: border-box;
  }

  .rich-html-root {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 16px;
    line-height: 1.6;
    color: #000000;
    text-align: left;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    padding: 24px;
    background-color: #f9f9f9;
  }

  h1 { font-size: 2em; font-weight: bold; margin: 0 0 0.5em; line-height: 1.2; }
  h2 { font-size: 1.5em; font-weight: bold; margin: 0 0 0.5em; line-height: 1.3; }
  h3 { font-size: 1.17em; font-weight: bold; margin: 0 0 0.5em; line-height: 1.4; }

  p { margin: 0 0 1em; }

  ul, ol { padding-left: 1.5em; margin: 0 0 1em; }
  ul { list-style-type: disc; }
  ol { list-style-type: decimal; }
  li { margin-bottom: 0.25em; }

  blockquote {
    border-left: 3px solid #bdbdbd;
    padding-left: 1em;
    margin: 0 0 1em;
    font-style: italic;
    color: #424242;
  }

  code {
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
    padding: 0.2em 0.4em;
    font-family: 'Courier New', Courier, monospace;
    font-size: 0.9em;
  }

  pre {
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 5px;
    padding: 0.75em 1em;
    font-family: 'Courier New', Courier, monospace;
    overflow-x: auto;
    margin: 0 0 1em;
  }
  pre code { background-color: transparent; padding: 0; border-radius: 0; }

  hr { border: none; border-top: 2px solid #e0e0e0; margin: 2em 0; }

  a { color: #1976d2; text-decoration: underline; cursor: pointer; }
  a:hover { color: #1565c0; }

  strong { font-weight: bold; }
  em { font-style: italic; }
  u { text-decoration: underline; }
  s { text-decoration: line-through; }

  img { max-width: 100%; height: auto; }
`;

const RichHtmlViewer: React.FC<RichHtmlViewerProps> = ({ content, sx = {} }) => {
  const user = useSelector((state: RootState) => state.profile.user);

  // Sanitize + resolve identity once per content/identity change.
  const safeHtml = useMemo(() => sanitizeHtml(content || '', user), [content, user]);

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <root.div>
        <style>{baselineStyles}</style>
        <div className="rich-html-root" dangerouslySetInnerHTML={{ __html: safeHtml }} />
      </root.div>
    </Box>
  );
};

export default RichHtmlViewer;
